import React, { forwardRef } from 'react'
import clsx from 'classnames'
import Image from 'next/image'
import { useLocale } from 'use-intl'
import { Text } from '@/shared/ui'
import { variantsCards } from '@/shared/constants/variants'
import { IImage } from '@/interfaces/common'
import { getDateLabel } from '@/shared/utils'
import { MotionLink } from '@/shared/ui/link'
import ArrowUpRightNewIcon from '@/public/arrow-right-up-new.svg'
import ArrowUpRightIcon from '@/public/arrow-right-up-new-small.svg'
import style from './media-about-card.module.scss'

export interface MediaAboutCard {
  title: string
  excerpt: string | null
  source: string | null
  date: string | null
  image: IImage | null
  index?: number
  url: string
}

interface MediaAboutCardProps extends Omit<React.HTMLProps<HTMLDivElement>, 'title' | 'type'>, MediaAboutCard {}

export const MediaAboutCard = forwardRef<HTMLAnchorElement, MediaAboutCardProps>((props, ref) => {
  const { className, image, date, title, index, url, excerpt, source } = props
  const currentLocale = useLocale()

  return (
    <MotionLink
      ref={ref}
      target="_blank"
      href={url}
      className={clsx(style.container, className)}
      variants={variantsCards}
      custom={index}
      viewport={{ amount: 0.45, once: true }}
    >
      <div className={style.poster}>
        {image ? (
          <Image src={image.url} fill alt="" style={{ objectFit: 'cover', objectPosition: 'center top' }} />
        ) : (
          <ArrowUpRightNewIcon className={style.placeholder} />
        )}
      </div>
      <div className={style.wrapper}>
        <Text className={style.date} type="d" color="dark-040" tagName="span">
          {getDateLabel({ date, locale: currentLocale })}
        </Text>
        <Text className={style.desc} type="t1" color="dark">
          {title}
        </Text>
        {!!excerpt && (
          <Text className={style.desc} type="t1" color="dark-070">
            {excerpt}
          </Text>
        )}
        {!!source && (
          <div className={style.button}>
            <Text className={style.label} type="d" tagName="span">
              {source}
            </Text>
            <ArrowUpRightIcon className={style.download} />
          </div>
        )}
      </div>
    </MotionLink>
  )
})

MediaAboutCard.displayName = 'MediaAboutCard'
